import { queryOptions, useQuery } from '@tanstack/react-query';
import { fetchAdminTemplateBuilderDetail } from '@/api/(admin)/questionnaire-templates';
import { fetchBuilderDetail } from '@/api/questionnaires';
import type {
  BuilderDetail,
  BuilderMode,
  BuilderScope,
  RawBuilderDetail,
} from '@/api/questionnaires/types';
import { normalizeBuilderDetail } from '../-utils/builderSerializers';

export const builderKeys = {
  all: ['questionnaires', 'builder'] as const,
  /** The tenant key is unchanged so nothing that already reads it moves; the
   *  admin scope gets its own segment, so an admin's template and a tenant's
   *  view of the same id never share an entry. */
  detail: (mode: BuilderMode, id: string, scope: BuilderScope = 'tenant') =>
    scope === 'admin'
      ? ([...builderKeys.all, 'admin', mode, id] as const)
      : ([...builderKeys.all, mode, id] as const),
};

/** The builder read (`GET /api/questionnaires/:id/builder`) returns
 *  `{ metadata, data }`. The still-legacy template *save* answers the detail at
 *  the top level, and `useSaveBuilder` feeds its response through the same
 *  normalizer — so this keeps tolerating both shapes. */
function unwrap(
  envelope: RawBuilderDetail | { data: RawBuilderDetail },
): RawBuilderDetail {
  return 'data' in envelope &&
    (envelope as { data: RawBuilderDetail }).data !== undefined
    ? (envelope as { data: RawBuilderDetail }).data
    : (envelope as RawBuilderDetail);
}

/** `mode` no longer selects an endpoint — both modes read the same route — but it
 *  stays in the cache key so a template and a questionnaire of the same id never
 *  share an entry. `scope` does select one: the admin scope reads the management
 *  twin of the route. */
export function builderDetailQueryOptions(
  mode: BuilderMode,
  id: string,
  scope: BuilderScope = 'tenant',
) {
  const read =
    scope === 'admin' ? fetchAdminTemplateBuilderDetail : fetchBuilderDetail;
  return queryOptions({
    queryKey: builderKeys.detail(mode, id, scope),
    queryFn: async (): Promise<BuilderDetail> => {
      const envelope = await read(id);
      return normalizeBuilderDetail(unwrap(envelope));
    },
  });
}

export function useBuilderDetailQuery(
  mode: BuilderMode,
  id: string,
  options?: { enabled?: boolean; scope?: BuilderScope },
) {
  return useQuery({
    ...builderDetailQueryOptions(mode, id, options?.scope ?? 'tenant'),
    enabled: options?.enabled ?? true,
  });
}
